import React, { useEffect, useRef, useState, FC, MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useDanceStore } from '../store/useDanceStore';
import { useStorageStore, SHARED_SEQUENCES_ID, UNSORTED_SEQUENCES_ID } from '../store/useStorageStore';
import { useAuthStore } from '../store/useAuthStore';
import { Play, Square, Save, Trash2, Copy, ClipboardPaste, Eraser, Dices, ChevronFirst, ChevronLast, ChevronsLeft, ChevronsRight, ChevronLeft, ChevronRight, Plus, Minus, FolderDown, SaveAll, ArrowLeft, ArrowRight, Maximize2, Minimize2, Timer, FolderOpen, MessageSquare } from 'lucide-react';
import { DanceState, Keyframe } from '../types';
import { PoseSaveToFrameButton } from './Sidebar';
import { ThumbnailView } from './ThumbnailView';
import { PoseNoteField } from './PoseNoteField';
import { PosePreviewBox } from './PosePreviewBox';
import { MusicPanel } from './MusicPanel';
import { MenuItem } from './ContextMenu';
import { TUTORIAL_MODULES } from '../utils/tutorialData';
import { useIsMobile } from '../hooks/useIsMobile';


// --- Shared Helper Functions ---

const getPlayheadPosition = (t: number, ticks: number[], tickLayout: {t: number, x: number, width: number}[]) => {
  if (ticks.length === 0 || tickLayout.length === 0) return 0;
  const nextIdx = ticks.findIndex(tick => tick > t);
  if (nextIdx === -1) {
    const last = tickLayout[tickLayout.length - 1];
    return last.x + last.width / 2;
  }
  if (nextIdx === 0) {
    const first = tickLayout[0];
    return (t / ticks[0]) * first.x;
  }
  const prevIdx = nextIdx - 1;
  const t0 = ticks[prevIdx];
  const t1 = ticks[nextIdx];
  const progress = (t - t0) / (t1 - t0);
  const x0 = tickLayout[prevIdx].x;
  const x1 = tickLayout[nextIdx].x;
  return x0 + progress * (x1 - x0);
};

// --- Performance Optimized Sub-components ---

const Playhead: FC<{ ticks: number[], tickLayout: {t: number, x: number, width: number}[] }> = React.memo(({ ticks, tickLayout }) => {
  const currentTime = useDanceStore(s => s.currentTime);
  const isPlaying = useDanceStore(s => s.isPlaying);
  const timelineZoom = useDanceStore(s => s.timelineZoom);

  if (!isPlaying) return null;

  const visualZoom = timelineZoom * 1.2;

  return (
    <div className="playhead-line" style={{
      position: 'absolute', top: 0,
      left: getPlayheadPosition(currentTime, ticks, tickLayout),
      width: Math.max(1, 2 * visualZoom),
      height: '100%', background: '#38bdf8',
      boxShadow: `0 0 ${8 * visualZoom}px #38bdf8`,
      zIndex: 10, pointerEvents: 'none'
    }} />
  );
});

const LiveTimeDisplay: FC<{ duration: number }> = React.memo(({ duration }) => {
  const currentTime = useDanceStore(s => s.currentTime);
  return (
    <div className="time-display" style={{ minWidth: '80px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600, fontSize: '0.85rem' }}>
      <span style={{ color: '#38bdf8' }}>{currentTime.toFixed(2)}s</span>
      <span style={{ color: 'rgba(255,255,255,0.2)', margin: '0 4px' }}>/</span>
      <span style={{ color: 'rgba(255,255,255,0.4)' }}>{duration.toFixed(0)}s</span>
    </div>
  );
});

interface TimelineFrameProps {
  time: number; left: number; width: number; isActive: boolean; isPlaying: boolean; isMeasureStart: boolean; keyframe?: Keyframe; isBeingDragged: boolean; isTarget: boolean; modifierState: { ctrl: boolean, shift: boolean }; dots: number[]; hasMusicLoaded: boolean; measures: number[]; beatInMeasure: number; isSelected: boolean; isCompact: boolean;
  onDragStart: (e: React.MouseEvent) => void; onSelect: (e: React.MouseEvent) => void; onContextMenu: (e: React.MouseEvent) => void; onHover: (hovered: boolean) => void; onToggleDot: (dotIdx: number) => void;
  onDropPose: (poseId: string) => void;
}

const TimelineFrame: FC<TimelineFrameProps> = React.memo(({
  time, left, width, isActive, isPlaying, isMeasureStart, keyframe, isBeingDragged, isTarget, modifierState, dots, hasMusicLoaded, measures, beatInMeasure, isSelected, isCompact, onDragStart, onSelect, onContextMenu, onHover, onToggleDot, onDropPose
}) => {
  const [isLibraryOver, setIsLibraryOver] = useState(false);



  let dragClass = "";
  if (isBeingDragged) {
    if (modifierState.ctrl) dragClass = "drag-copy-source"; else dragClass = "drag-move-source";
  }
  if (isTarget) {
    const isOverwrite = !!keyframe;
    if (isOverwrite) {
      if (modifierState.ctrl) dragClass = "drag-copy-target overwrite"; else dragClass = "drag-move-target overwrite";
    } else if (!isBeingDragged) {
      if (modifierState.ctrl) dragClass = "drag-copy-target"; else dragClass = "drag-move-target";
    }
  }

  return (
    <div
      id={`timeline-frame-${time.toFixed(3)}`}
      className={`timeline-marker ${isActive ? 'active' : ''} ${isSelected ? 'selected' : ''} ${dragClass} ${isMeasureStart ? 'measure-start' : ''} ${isLibraryOver ? 'library-drop-target' : ''} ${isCompact ? 'compact' : ''}`}
      onMouseDown={(e) => {
        if (e.button !== 0) return; // Only left-click
        e.preventDefault(); // Prevent browser default drag/selection
        e.stopPropagation();
        
        const isCtrlClick = e.ctrlKey || e.metaKey;
        if (isSelected) {
          if (keyframe) onDragStart(e);
          if (!keyframe && isCtrlClick) {
            onSelect(e);
          }
        } else {
          onSelect(e);
          if (keyframe) onDragStart(e);
        }
      }}
      onContextMenu={(e) => onContextMenu(e)}
      onDragEnter={(e) => {
        if (e.dataTransfer.types.includes('sourceType')) {
          setIsLibraryOver(true);
        }
      }}
      onDragLeave={() => setIsLibraryOver(false)}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
      }}
      onDrop={(e) => {
        e.preventDefault();
        setIsLibraryOver(false);
        const type = e.dataTransfer.getData('sourceType');
        const id = e.dataTransfer.getData('sourceId');
        if (type === 'pose' && id) {
          onDropPose(id);
        }
      }}
      onMouseEnter={() => onHover(true)} onMouseLeave={() => onHover(false)}
      style={{
        left: `${left}px`,
        width: `${width - (8 * (width / 64))}px`,
        position: 'absolute',
        transform: 'translateX(-50%)',
        zIndex: (isActive && !isPlaying) ? 30 : 6,
        pointerEvents: 'auto',
        cursor: 'pointer',
        background: keyframe ? 'rgba(30, 41, 59, 0.9)' : 'rgba(30, 41, 59, 0.9)',
        border: (isActive && !isPlaying) ? '2px solid #fbbf24' : isSelected ? '1px solid #fbbf24' : keyframe ? '1px solid #38bdf8' : '1px solid rgba(51, 65, 85, 0.5)',
        boxShadow: (isActive && !isPlaying) ? '0 0 15px rgba(251, 191, 36, 0.5)' : 'none'
      }}
    >
      <div className="marker-dots top">
        {isCompact ? (
          <div className={`dot ${isMeasureStart ? 'measure-dot' : ''}`} />
        ) : (
          [0, 1, 2, 3].map(i => (
            <div 
              key={i} 
              id={i === 0 && isMeasureStart ? `timeline-measure-dot-${time.toFixed(3)}` : undefined}
              className={`dot ${i === 0 && isMeasureStart ? 'measure-dot' : ''}`} 
            />
          ))
        )}
      </div>
      <div className="marker-dots bottom">
        {isCompact ? (
          (() => {
            const hasNotes = !!(keyframe?.pose?.notes && keyframe.pose.notes.trim() !== '');
            const hasKeyframeNote = !!(keyframe?.note && keyframe.note.trim() !== '');
            const hasActiveDots = dots.some(d => d !== 1 && d !== 2) || hasNotes || hasKeyframeNote;
            return <div className={`dot ${hasActiveDots ? 'has-data' : ''}`} />;
          })()
        ) : (
          [0, 1, 2, 3].map(i => {
            const hasNotes = !!(keyframe?.pose?.notes && keyframe.pose.notes.trim() !== '');
            const hasKeyframeNote = !!(keyframe?.note && keyframe.note.trim() !== '');
            const isDotSelected = i === 1 ? hasNotes : i === 2 ? hasKeyframeNote : dots.includes(i);
            const isClickable = i !== 1 && i !== 2;
            return (
              <div
                key={i}
                id={`timeline-dot-${time.toFixed(3)}-${i}`}
                className={`dot dot-${i} ${isClickable ? 'clickable' : ''} ${isDotSelected ? 'selected' : ''}`}
                onMouseDown={isClickable ? (e) => { e.stopPropagation(); onToggleDot(i); } : undefined}
              />
            );
          })
        )}
      </div>
      {keyframe && keyframe.pose ? (
        <>
          <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', borderRadius: 'inherit', pointerEvents: 'none' }}>
            <ThumbnailView pose={keyframe.pose} cameraState={keyframe.cameraState} />
          </div>
          <div className="frame-label">{time.toFixed(1)}s</div>
        </>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          {hasMusicLoaded && (
            <div className={`tick-beat-label ${isMeasureStart ? 'measure-start' : ''}`}>
              {isMeasureStart ? `B${measures.indexOf(time) + 1}` : `♩${beatInMeasure}`}
            </div>
          )}
          <div className="tick-time-label">{time.toFixed(1)}</div>
        </div>
      )}
    </div>
  );
});

// Sub-components moved to top for hoisting stability

export const Timeline: FC = () => {
  const { t } = useTranslation();
  const { isMobile, isLandscapeMobile } = useIsMobile();
  const keyframes = useDanceStore((state: DanceState) => state.keyframes);
  // Remove direct currentTime subscription to prevent re-renders on every frame
  // const currentTime = useDanceStore((state: DanceState) => state.currentTime);
  const duration = useDanceStore((state: DanceState) => state.duration);
  const metronomeInterval = useDanceStore((state: DanceState) => state.metronomeInterval);
  const isPlaying = useDanceStore((state: DanceState) => state.isPlaying);
  const activeModule = useDanceStore((state: DanceState) => state.activeModule);

  const setCurrentTime = useDanceStore((state: DanceState) => state.setCurrentTime);
  const setIsPlaying = useDanceStore((state: DanceState) => state.setIsPlaying);
  const addKeyframe = useDanceStore((state: DanceState) => state.addKeyframe);
  const removeKeyframe = useDanceStore((state: DanceState) => state.removeKeyframe);
  const clearTimeline = useDanceStore((state: DanceState) => state.clearTimeline);

  const setDuration = useDanceStore((state: DanceState) => state.setDuration);
  const frameMarkers = useDanceStore((state: DanceState) => state.frameMarkers);
  const setMetronomeInterval = useDanceStore((state: DanceState) => state.setMetronomeInterval);
  const updateKeyframeTime = useDanceStore((state: DanceState) => state.updateKeyframeTime);
  const copiedPose = useDanceStore((state: DanceState) => state.copiedPose);
  const copyPose = useDanceStore((state: DanceState) => state.copyPose);
  const pastePoseAndSave = useDanceStore((state: DanceState) => state.pastePoseAndSave);
  const copyKeyframe = useDanceStore((state: DanceState) => state.copyKeyframe);
  const moveMultipleKeyframes = useDanceStore((state: DanceState) => state.moveMultipleKeyframes);
  const copyMultipleKeyframes = useDanceStore((state: DanceState) => state.copyMultipleKeyframes);
  const rippleMoveMultipleKeyframes = useDanceStore((state: DanceState) => state.rippleMoveMultipleKeyframes);
  const rippleCopyMultipleKeyframes = useDanceStore((state: DanceState) => state.rippleCopyMultipleKeyframes);
  const undo = useDanceStore((state: DanceState) => state.undo);
  const redo = useDanceStore((state: DanceState) => state.redo);
  const generateRandomPoseWorkspace = useDanceStore((state: DanceState) => state.generateRandomPoseWorkspace);
  const randomnessAmount = useDanceStore((state: DanceState) => state.randomnessAmount);
  const setRandomnessAmount = useDanceStore((state: DanceState) => state.setRandomnessAmount);
  const applyPoseToTime = useDanceStore((state: DanceState) => state.applyPoseToTime);
  const allPoses = useStorageStore(state => state.poses);

  const selectedFrameTimes = useDanceStore(state => state.selectedFrameTimes);
  const setSelectedFrameTimes = useDanceStore(state => state.setSelectedFrameTimes);
  const toggleFrameSelection = useDanceStore(state => state.toggleFrameSelection);
  const deleteSelectedFrames = useDanceStore(state => state.deleteSelectedFrames);
  const clearSelectedFrames = useDanceStore(state => state.clearSelectedFrames);
  const copySelectedFrames = useDanceStore(state => state.copySelectedFrames);
  const insertCopiedFrames = useDanceStore(state => state.insertCopiedFrames);
  const insertBlankFrames = useDanceStore(state => state.insertBlankFrames);
  const copiedFrames = useDanceStore(state => state.copiedFrames);
  const openContextMenu = useDanceStore(state => state.openContextMenu);
  const confirmAction = useDanceStore(state => state.confirmAction);

  // Music state
  const hasMusicLoaded = useDanceStore((state: DanceState) => state.hasMusicLoaded);
  const beats = useDanceStore((state: DanceState) => state.beats);
  const measures = useDanceStore((state: DanceState) => state.measures);
  const bpm = useDanceStore((state: DanceState) => state.bpm);
  const audioUrl = useDanceStore((state: DanceState) => state.audioUrl);
  const waveformData = useDanceStore((state: DanceState) => state.waveformData);
  const musicVolume = useDanceStore((state: DanceState) => state.musicVolume);
  const toggleFrameMarker = useDanceStore((state: DanceState) => state.toggleFrameMarker);
  const hasUnsavedChanges = useDanceStore((state: DanceState) => state.hasUnsavedChanges);
  const hasUnsavedSequenceChanges = useDanceStore((state: DanceState) => state.hasUnsavedSequenceChanges);
  const loadedSequence = useDanceStore((state: DanceState) => state.loadedSequence);
  const setHasUnsavedSequenceChanges = useDanceStore((state: DanceState) => state.setHasUnsavedSequenceChanges);
  const setLoadedSequence = useDanceStore((state: DanceState) => state.setLoadedSequence);
  const autoSaveEnabled = useDanceStore((state: DanceState) => state.autoSaveEnabled);
  const setAutoSaveEnabled = useDanceStore((state: DanceState) => state.setAutoSaveEnabled);
  const audioFileName = useDanceStore((state: DanceState) => state.audioFileName);
  const audioHash = useDanceStore((state: DanceState) => state.audioHash);
  const audioSourceUrl = useDanceStore((state: DanceState) => state.audioSourceUrl);
  const footMarkers = useDanceStore((state: DanceState) => state.footMarkers);
  const locationMarkers = useDanceStore((state: DanceState) => state.locationMarkers);
  const actingSource = useDanceStore((state: DanceState) => state.actingSource);
  const user = useAuthStore(state => state.user);
  const isSharedSequence = !!(
    (loadedSequence?.userId && user?.uid && loadedSequence.userId !== user.uid) ||
    (user?.email && loadedSequence?.collaborators?.includes(user.email))
  );

  const saveSequenceStore = useStorageStore(state => state.saveSequence);
  const selectedFolderId = useStorageStore(state => state.selectedSequenceFolderId);
  const folders = useStorageStore(state => state.folders);

  const targetFolderId = selectedFolderId === SHARED_SEQUENCES_ID ? UNSORTED_SEQUENCES_ID : selectedFolderId;
  const sequenceFolder = targetFolderId ? folders.find(f => f.id === targetFolderId) : null;
  const sequenceFolderName = targetFolderId === null ? 'Root' : (sequenceFolder?.name ?? 'Root');
  const sequenceFolderPath = targetFolderId === null ? 'Root' : (() => {
    const path: string[] = [];
    let curr: string | null = targetFolderId;
    while (curr) {
      const f = folders.find(x => x.id === curr);
      if (!f) break;
      path.unshift(f.name);
      curr = f.parentId;
    }
    return path.join(' > ');
  })();
  const tutorialProgress = useStorageStore(state => state.tutorialProgress);
  const currentModuleId = tutorialProgress.currentModuleId;
  const currentModule = TUTORIAL_MODULES.find(m => m.id === currentModuleId);
  const currentStep = currentModule?.steps[tutorialProgress.currentStepIndex];
  const isTutorialStepActive = !!(currentStep && (currentStep.targetId === 'timeline-randomness-slider-container' || currentStep.targetId === 'timeline-random-pose-btn'));
  const isZoomTutorialActive = !!(currentStep && currentStep.targetId && (currentStep.targetId.includes('timeline-zoom') || currentStep.targetId.includes('timeline-compact')));
  const isSettingsTutorialActive = !!(currentStep && currentStep.targetId && (
    currentStep.targetId.includes('timeline-duration-input') || 
    currentStep.targetId.includes('timeline-tick-interval-select')
  ));
  const audioDuration = useDanceStore((state: DanceState) => state.audioDuration);
  const openInputDialog = useDanceStore(state => state.openInputDialog);
  const setKeyframeNote = useDanceStore((state: DanceState) => state.setKeyframeNote);
  const setKeyframePoseNotes = useDanceStore((state: DanceState) => state.setKeyframePoseNotes);
  const deleteKeyframeNote = useDanceStore((state: DanceState) => state.deleteKeyframeNote);


  const panelRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const waveformCanvasRef = useRef<HTMLCanvasElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedKeyframeTime, setDraggedKeyframeTime] = useState<number | null>(null);
  const [dragTargetTime, setDragTargetTime] = useState<number | null>(null);
  const [dragMousePos, setDragMousePos] = useState<{ x: number, y: number } | null>(null);
  const [isDragInsert, setIsDragInsert] = useState(false);
  const isDragInsertRef = useRef(false);
  const [insertLineX, setInsertLineX] = useState<number | null>(null);
  const [modifierState, setModifierState] = useState({ ctrl: false, shift: false });
  const [hoveredTime, setHoveredTime] = useState<number | null>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMouseOverFrameRef = useRef(false);
  const isMouseOverTooltipRef = useRef(false);

  const [isEditingPoseNote, setIsEditingPoseNote] = useState(false);
  const [isEditingKfNote, setIsEditingKfNote] = useState(false);

  const handleFrameMouseEnter = (tickTime: number) => {
    isMouseOverFrameRef.current = true;
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setHoveredTime(tickTime);
  };

  const handleFrameMouseLeave = () => {
    isMouseOverFrameRef.current = false;
    if (isEditingPoseNote || isEditingKfNote) {
      return;
    }
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => {
      if (!isMouseOverTooltipRef.current && !isMouseOverFrameRef.current) {
        setHoveredTime(null);
      }
    }, 200);
  };

  const handlePopupMouseEnter = () => {
    isMouseOverTooltipRef.current = true;
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  const handlePopupMouseLeave = () => {
    isMouseOverTooltipRef.current = false;
    if (isEditingPoseNote || isEditingKfNote) {
      return;
    }
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => {
      if (!isMouseOverTooltipRef.current && !isMouseOverFrameRef.current) {
        setHoveredTime(null);
      }
    }, 200);
  };

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // When editing state returns to false, close tooltip if mouse is outside
  useEffect(() => {
    if (!isEditingPoseNote && !isEditingKfNote) {
      if (!isMouseOverFrameRef.current && !isMouseOverTooltipRef.current) {
        setHoveredTime(null);
      }
    }
  }, [isEditingPoseNote, isEditingKfNote]);

  // Click outside tooltip handler to cancel editing and close the popup
  useEffect(() => {
    if (!isEditingPoseNote && !isEditingKfNote) return;

    const handleDocumentClick = (e: globalThis.MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.pose-preview-popup')) {
        setIsEditingPoseNote(false);
        setIsEditingKfNote(false);
        setHoveredTime(null);
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, [isEditingPoseNote, isEditingKfNote]);

  useEffect(() => {
    if (hoveredTime === null) {
      setIsEditingPoseNote(false);
      setIsEditingKfNote(false);
    }
  }, [hoveredTime]);

  const [trackScroll, setTrackScroll] = useState(0);
  const [needsControlScroll, setNeedsControlScroll] = useState(false);
  const [isRandomHovered, setIsRandomHovered] = useState(false);
  const [isZoomHovered, setIsZoomHovered] = useState(false);
  const [isSettingsHovered, setIsSettingsHovered] = useState(false);
  const [isSaveHovered, setIsSaveHovered] = useState(false);

  const zoomOpenTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const zoomCloseTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const randomOpenTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const randomCloseTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const settingsOpenTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const settingsCloseTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSettingsEnter = () => {
    if (settingsCloseTimeout.current) clearTimeout(settingsCloseTimeout.current);
    if (settingsOpenTimeout.current) clearTimeout(settingsOpenTimeout.current);
    
    settingsOpenTimeout.current = setTimeout(() => {
      setIsZoomHovered(false);
      setIsRandomHovered(false);
      setIsSettingsHovered(true);
    }, 150);
  };

  const handleSettingsLeave = () => {
    if (settingsOpenTimeout.current) clearTimeout(settingsOpenTimeout.current);
    if (settingsCloseTimeout.current) clearTimeout(settingsCloseTimeout.current);
    
    settingsCloseTimeout.current = setTimeout(() => {
      setIsSettingsHovered(false);
    }, 250);
  };

  const handleZoomEnter = () => {
    if (zoomCloseTimeout.current) clearTimeout(zoomCloseTimeout.current);
    if (zoomOpenTimeout.current) clearTimeout(zoomOpenTimeout.current);
    
    zoomOpenTimeout.current = setTimeout(() => {
      setIsSettingsHovered(false);
      setIsRandomHovered(false);
      setIsZoomHovered(true);
    }, 150);
  };

  const handleZoomLeave = () => {
    if (zoomOpenTimeout.current) clearTimeout(zoomOpenTimeout.current);
    if (zoomCloseTimeout.current) clearTimeout(zoomCloseTimeout.current);
    
    zoomCloseTimeout.current = setTimeout(() => {
      setIsZoomHovered(false);
    }, 250);
  };

  const handleRandomEnter = () => {
    if (randomCloseTimeout.current) clearTimeout(randomCloseTimeout.current);
    if (randomOpenTimeout.current) clearTimeout(randomOpenTimeout.current);
    
    randomOpenTimeout.current = setTimeout(() => {
      setIsSettingsHovered(false);
      setIsZoomHovered(false);
      setIsRandomHovered(true);
    }, 150);
  };

  const handleRandomLeave = () => {
    if (randomOpenTimeout.current) clearTimeout(randomOpenTimeout.current);
    if (randomCloseTimeout.current) clearTimeout(randomCloseTimeout.current);
    
    randomCloseTimeout.current = setTimeout(() => {
      setIsRandomHovered(false);
    }, 250);
  };

  useEffect(() => {
    return () => {
      if (zoomOpenTimeout.current) clearTimeout(zoomOpenTimeout.current);
      if (zoomCloseTimeout.current) clearTimeout(zoomCloseTimeout.current);
      if (randomOpenTimeout.current) clearTimeout(randomOpenTimeout.current);
      if (randomCloseTimeout.current) clearTimeout(randomCloseTimeout.current);
      if (settingsOpenTimeout.current) clearTimeout(settingsOpenTimeout.current);
      if (settingsCloseTimeout.current) clearTimeout(settingsCloseTimeout.current);
    };
  }, []);

  const timelineZoom = useDanceStore(state => state.timelineZoom);
  const visualZoom = timelineZoom * 1.2;
  const setTimelineZoom = useDanceStore(state => state.setTimelineZoom);
  const isTimelineCompact = useDanceStore(state => state.isTimelineCompact);
  const setIsTimelineCompact = useDanceStore(state => state.setIsTimelineCompact);
  const beatSubdivision = useDanceStore(state => state.beatSubdivision);
  const setBeatSubdivision = useDanceStore(state => state.setBeatSubdivision);
  const isReadOnly = useDanceStore(state => state.isReadOnly);

  const [tempDuration, setTempDuration] = useState<string | number>(duration);

  useEffect(() => {
    setTempDuration(duration);
  }, [duration]);

  const willLoseWorkOnDurationChange = (newD: number): boolean => {
    if (newD >= duration) return false;
    const hasKeyframesBeyond = keyframes.some(kf => kf.time > newD);
    if (hasKeyframesBeyond) return true;
    const hasMarkersBeyond = Object.entries(frameMarkers).some(([tStr, dots]) => {
      const t = parseFloat(tStr);
      return t > newD && dots.length > 0;
    });
    if (hasMarkersBeyond) return true;
    return false;
  };

  const willLoseWorkOnMetronomeIntervalChange = (newInt: number): boolean => {
    if (newInt <= 0) return false;
    const snappedTimes = new Set<string>();
    for (const kf of keyframes) {
      const snappedT = Math.round(kf.time / newInt) * newInt;
      if (snappedT > duration) return true;
      const key = snappedT.toFixed(3);
      if (snappedTimes.has(key)) return true;
      snappedTimes.add(key);
    }
    for (const [timeStr, dots] of Object.entries(frameMarkers)) {
      if (dots.length === 0) continue;
      const t = parseFloat(timeStr);
      const snappedT = Math.round(t / newInt) * newInt;
      if (snappedT > duration) return true;
    }
    return false;
  };

  const willLoseWorkOnBeatSubdivisionChange = (newSub: number): boolean => {
    if (beats.length === 0) return false;
    const newTicks: number[] = [];
    if (beats[0] > 0.05) newTicks.push(0);
    beats.forEach((b, i) => {
      if (b <= duration) {
        newTicks.push(b);
        if (newSub > 1 && i < beats.length - 1) {
          const nextB = beats[i + 1];
          for (let s = 1; s < newSub; s++) {
            const subT = b + (nextB - b) * (s / newSub);
            if (subT <= duration) newTicks.push(subT);
          }
        }
      }
    });

    if (newTicks.length === 0) return false;

    const snapToClosestTick = (t: number): number => {
      return newTicks.reduce((prev, curr) => Math.abs(curr - t) < Math.abs(prev - t) ? curr : prev);
    };

    const snappedTimes = new Set<string>();
    for (const kf of keyframes) {
      const snappedT = snapToClosestTick(kf.time);
      if (snappedT > duration) return true;
      const key = snappedT.toFixed(3);
      if (snappedTimes.has(key)) return true;
      snappedTimes.add(key);
    }
    for (const [timeStr, dots] of Object.entries(frameMarkers)) {
      if (dots.length === 0) continue;
      const t = parseFloat(timeStr);
      const snappedT = snapToClosestTick(t);
      if (snappedT > duration) return true;
    }
    return false;
  };

  const handleDurationChange = (newD: number) => {
    if (isNaN(newD) || newD < 1 || newD > 60) {
      setTempDuration(duration);
      return;
    }
    if (newD < duration) {
      const willLose = willLoseWorkOnDurationChange(newD);
      if (willLose) {
        confirmAction({
          title: t('timeline.reduce_duration_confirm_title', { defaultValue: "Reduce Duration?" }),
          message: t('timeline.reduce_duration_confirm_message', { defaultValue: "Reducing the duration will delete keyframes and markers beyond the new duration. Are you sure you want to proceed?" }),
          confirmLabel: t('common.proceed', { defaultValue: "Proceed" }),
          cancelLabel: t('common.cancel'),
          onConfirm: () => setDuration(newD),
          onCancel: () => setTempDuration(duration)
        });
        return;
      }
    }
    setDuration(newD);
  };

  const handleTickIntervalChange = (val: number) => {
    if (hasMusicLoaded) {
      const newSub = val;
      if (newSub === beatSubdivision) return;
      const willLose = willLoseWorkOnBeatSubdivisionChange(newSub);
      if (willLose) {
        confirmAction({
          title: t('timeline.change_subdivision_confirm_title', { defaultValue: "Change Beat Subdivision?" }),
          message: t('timeline.change_subdivision_confirm_message', { defaultValue: "Changing the beat subdivision will snap keyframes to the new beat subdivision grid, which will overwrite/delete overlapping keyframes. Are you sure you want to proceed?" }),
          confirmLabel: t('common.proceed', { defaultValue: "Proceed" }),
          cancelLabel: t('common.cancel'),
          onConfirm: () => setBeatSubdivision(newSub)
        });
      } else {
        setBeatSubdivision(newSub);
      }
    } else {
      const newInt = val;
      if (newInt === metronomeInterval) return;
      if (newInt <= 0) {
        setMetronomeInterval(newInt);
        return;
      }
      const willLose = willLoseWorkOnMetronomeIntervalChange(newInt);
      if (willLose) {
        confirmAction({
          title: t('timeline.change_grid_confirm_title', { defaultValue: "Change Grid Interval?" }),
          message: t('timeline.change_grid_confirm_message', { defaultValue: "Changing the grid interval will snap keyframes to the new grid, which will overwrite/delete overlapping keyframes. Are you sure you want to proceed?" }),
          confirmLabel: t('common.proceed', { defaultValue: "Proceed" }),
          cancelLabel: t('common.cancel'),
          onConfirm: () => setMetronomeInterval(newInt)
        });
      } else {
        setMetronomeInterval(newInt);
      }
    }
  };
  
  const TICK_WIDTH = 64 * visualZoom; // Base width for expanded/populated frames
  const COMPACT_WIDTH = 20 * visualZoom; // Width for collapsed empty frames

  useEffect(() => {
    if (draggedKeyframeTime === null) return;

    const handleGlobalMouseMove = (e: globalThis.MouseEvent) => {
      setDragMousePos({ x: e.clientX, y: e.clientY });
      handleTrackScrub(e);
    };

    const handleGlobalMouseUp = (e: globalThis.MouseEvent) => {
      if (draggedKeyframeTime !== null && dragTargetTime !== null && draggedKeyframeTime !== dragTargetTime) {
        const delta = dragTargetTime - draggedKeyframeTime;
        const isDragCopy = e.ctrlKey || e.metaKey || modifierState.ctrl;
        const isDragInsert = isDragInsertRef.current;
        
        const isDraggedInSelection = selectedFrameTimes.some(t => Math.abs(t - draggedKeyframeTime) < 0.05);
        const timesToDrag = isDraggedInSelection
          ? selectedFrameTimes.filter(t => keyframes.some(kf => Math.abs(kf.time - t) < 0.05))
          : [draggedKeyframeTime];

        if (isDragInsert) {
          if (isDragCopy) {
            rippleCopyMultipleKeyframes(timesToDrag, dragTargetTime);
          } else {
            rippleMoveMultipleKeyframes(timesToDrag, dragTargetTime);
          }
        } else {
          const destinationTimes = timesToDrag.map(t => t + delta);
          const willOverwrite = keyframes.some(kf => 
            destinationTimes.some(dt => Math.abs(dt - kf.time) < 0.05) &&
            !timesToDrag.some(td => Math.abs(td - kf.time) < 0.05)
          );

          if (willOverwrite) {
            setIsDragging(false);
            setDraggedKeyframeTime(null);
            setDragTargetTime(null);
            setDragMousePos(null);

            confirmAction({
              title: isDragCopy ? t('timeline.overwrite_copy_title', { defaultValue: "Overwrite Keyframes?" }) : t('timeline.overwrite_move_title', { defaultValue: "Overwrite Keyframes?" }),
              message: isDragCopy 
                ? t('timeline.overwrite_copy_message', { defaultValue: "Copying these frames will overwrite existing keyframes. Do you want to proceed?" })
                : t('timeline.overwrite_move_message', { defaultValue: "Moving these frames will overwrite existing keyframes. Do you want to proceed?" }),
              confirmLabel: t('common.proceed', { defaultValue: "Proceed" }),
              cancelLabel: t('common.cancel'),
              onConfirm: () => {
                if (isDragCopy) {
                  copyMultipleKeyframes(timesToDrag, delta);
                } else {
                  moveMultipleKeyframes(timesToDrag, delta);
                }
              }
            });
            return;
          }

          if (isDragCopy) {
            copyMultipleKeyframes(timesToDrag, delta);
          } else {
            moveMultipleKeyframes(timesToDrag, delta);
          }
        }
      } else {
        if (draggedKeyframeTime !== null) {
          const isCtrlClick = e.ctrlKey || e.metaKey;
          if (isCtrlClick) {
            toggleFrameSelection(draggedKeyframeTime, true);
          } else {
            const hasModifiers = e.ctrlKey || e.metaKey || e.shiftKey;
            if (!hasModifiers) {
              safeMoveTo(draggedKeyframeTime);
            }
          }
        }
      }
      setIsDragging(false);
      setDraggedKeyframeTime(null);
      setDragTargetTime(null);
      setDragMousePos(null);
      setIsDragInsert(false);
      setInsertLineX(null);
      isDragInsertRef.current = false;
    };

    window.addEventListener('mousemove', handleGlobalMouseMove);
    window.addEventListener('mouseup', handleGlobalMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [draggedKeyframeTime, dragTargetTime, modifierState, selectedFrameTimes, keyframes, handleTrackScrub, safeMoveTo, confirmAction, t]);

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    const handleWheelZoom = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const currentZoom = useDanceStore.getState().timelineZoom;
        // Invert delta: scroll up (negative) to zoom in, scroll down (positive) to zoom out
        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        useDanceStore.getState().setTimelineZoom(currentZoom + delta);
      }
    };

    panel.addEventListener('wheel', handleWheelZoom, { passive: false });
    return () => panel.removeEventListener('wheel', handleWheelZoom);
  }, []);

  const ticks: number[] = [];
  if (hasMusicLoaded && beats.length > 0) {
    if (beats[0] > 0.05) ticks.push(0);
    beats.forEach((b, i) => {
      if (b <= duration) {
        ticks.push(b);
        // Add rhythmic subdivisions between beats
        if (beatSubdivision > 1 && i < beats.length - 1) {
          const nextB = beats[i + 1];
          for (let s = 1; s < beatSubdivision; s++) {
            const subT = b + (nextB - b) * (s / beatSubdivision);
            if (subT <= duration) ticks.push(subT);
          }
        }
      }
    });
  } else if (metronomeInterval > 0) {
    for (let t = 0; t <= duration; t += metronomeInterval) {
      ticks.push(t);
    }
  }

  // Specialized selector for snappedPlayhead to prevent Timeline re-renders during playback
  const snappedPlayhead = useDanceStore(state => {
    if (state.isPlaying) return -1;
    const t = state.currentTime;
    if (ticks.length === 0) return t;
    let closestTick = ticks[0], closestDist = Math.abs(t - ticks[0]);
    for (let i = 1; i < ticks.length; i++) {
      const dist = Math.abs(t - ticks[i]);
      if (dist < closestDist) { closestDist = dist; closestTick = ticks[i]; }
    }
    return closestTick;
  });

  const tickLayout = React.useMemo(() => {
    let currentX = 0;
    return ticks.map((t) => {
      const isKeyframe = keyframes.some((kf: Keyframe) => Math.abs(kf.time - t) < 0.05);
      const isActive = !isPlaying && (actingSource.type === 'timeline' || actingSource.type === 'library') && Math.abs(t - snappedPlayhead) < 0.05;
      
      let width = TICK_WIDTH;
      if (isTimelineCompact && !isKeyframe && !isActive) {
        width = COMPACT_WIDTH;
      }
      const x = currentX + width / 2;
      currentX += width;
      return { t, x, width };
    });
  }, [ticks, keyframes, snappedPlayhead, isTimelineCompact, TICK_WIDTH, COMPACT_WIDTH, isPlaying, actingSource.type]);

  const totalTrackWidth = tickLayout.length > 0 
    ? (tickLayout[tickLayout.length - 1].x + tickLayout[tickLayout.length - 1].width / 2) 
    : ticks.length * TICK_WIDTH;

  // Playhead position logic moved to Playhead component


  useEffect(() => {
    if (!waveformCanvasRef.current || waveformData.length === 0) return;
    const canvas = waveformCanvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const trackWidth = totalTrackWidth;
    const internalWidth = Math.min(trackWidth, 8192); // Increased limit for detailed zoom
    canvas.width = internalWidth;
    canvas.height = 59;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const barWidth = canvas.width / waveformData.length;
    const centerY = canvas.height / 2;
    const maxHeight = canvas.height * 0.45;
    for (let i = 0; i < waveformData.length; i++) {
      const amp = waveformData[i];
      const barH = amp * maxHeight;
      const timeAtI = (i / waveformData.length) * duration;
      const x = (getPlayheadPosition(timeAtI, ticks, tickLayout) / totalTrackWidth) * canvas.width;
      const hue = 250 + (i / waveformData.length) * 40;
      ctx.fillStyle = `hsla(${hue}, 60%, 60%, 0.25)`;
      ctx.fillRect(x, centerY - barH, Math.max(1, barWidth), barH * 2);
    }
    if (measures.length > 0) {
      ctx.strokeStyle = 'rgba(167, 139, 250, 0.5)';
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      measures.forEach(m => {
        const x = (m / duration) * canvas.width;
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, canvas.height); ctx.stroke();
      });
      ctx.setLineDash([]);
    }
  }, [waveformData, ticks.length, measures, duration]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = musicVolume;
  }, [musicVolume]);

  const currentTimeRef = useRef(useDanceStore.getState().currentTime);
  useEffect(() => {
    return useDanceStore.subscribe(state => {
      currentTimeRef.current = state.currentTime;
    });
  }, []);

  useEffect(() => {
    if (!isPlaying) {
      if (audioRef.current && hasMusicLoaded) audioRef.current.pause();
      return;
    }
    if (audioRef.current && hasMusicLoaded && audioUrl) {
      audioRef.current.currentTime = currentTimeRef.current;
      audioRef.current.play().catch(() => { });
    }
    let animationFrame: number;
    let lastTime = performance.now();
    let lastAudioTime = 0;
    let lastSyncTime = performance.now();

    const loop = (time: number) => {
      let t = 0;
      if (hasMusicLoaded && audioRef.current) {
        const audioTime = audioRef.current.currentTime;
        if (audioTime !== lastAudioTime) {
          lastAudioTime = audioTime;
          lastSyncTime = time;
        }
        // Interpolate between audio updates (which happen at ~10-20Hz) to get smooth 60fps
        const delta = (time - lastSyncTime) / 1000;
        let smoothTime = audioTime + delta;

        if (smoothTime >= duration || audioRef.current.ended) {
          audioRef.current.currentTime = 0;
          setCurrentTime(0);
          t = 0;
        } else {
          setCurrentTime(smoothTime);
          t = smoothTime;
        }
      } else {
        const delta = (time - lastTime) / 1000;
        lastTime = time;
        if (delta > 0) {
          let nextTime = currentTimeRef.current + delta;
          if (nextTime > duration) nextTime = 0;
          setCurrentTime(nextTime);
          t = nextTime;
        }
      }

      // Auto-scroll logic integrated into loop for smooth tracking
      if (trackRef.current && ticks.length > 0 && !isDragging && draggedKeyframeTime === null) {
        const playheadX = getPlayheadPosition(t, ticks, tickLayout);
        const trackEl = trackRef.current;
        const viewLeft = trackEl.scrollLeft;
        const viewRight = viewLeft + trackEl.clientWidth;
        const margin = 80;
        if (playheadX < viewLeft + margin || playheadX > viewRight - margin) {
          const targetScroll = playheadX - trackEl.clientWidth / 2;
          trackEl.scrollLeft = targetScroll;
        }
      }

      animationFrame = requestAnimationFrame(loop);
    };
    animationFrame = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(animationFrame);
      if (audioRef.current && hasMusicLoaded) audioRef.current.pause();
    };
  }, [isPlaying, duration, setCurrentTime, hasMusicLoaded, audioUrl]);

  const moveTo = (time: number, triggerScroll: boolean = true) => {
    setCurrentTime(time);
    toggleFrameSelection(time, false);
    if (audioRef.current && hasMusicLoaded) audioRef.current.currentTime = time;

    if (triggerScroll && trackRef.current && ticks.length > 0) {
      const playheadX = getPlayheadPosition(time, ticks, tickLayout);
      const trackEl = trackRef.current;
      trackEl.scrollLeft = playheadX - trackEl.clientWidth / 2;
    }
  };

  function safeMoveTo(time: number, triggerScroll: boolean = false) {
    // If we are editing a library pose, we allow free navigation (Sticky Workspace)
    if (actingSource.type === 'library') {
      moveTo(time, triggerScroll);
      return;
    }

    // Bypass confirmation during any active tutorial module to prevent flow breaks
    if (currentModuleId) {
      moveTo(time, triggerScroll);
      return;
    }

    if (hasUnsavedChanges && !isPlaying) {
      if (autoSaveEnabled) {
        addKeyframe(undefined, currentTimeRef.current);
        moveTo(time, triggerScroll);
      } else {
        confirmAction({
          title: t('timeline.unsaved_changes_title'),
          message: t('timeline.unsaved_changes_message'),
          confirmLabel: t('common.confirm'),
          cancelLabel: t('common.cancel'),
          showOptions: true,
          options: [
            { key: 'save', label: t('timeline.save_and_switch'), desc: t('timeline.save_desc') },
            { key: 'move', label: t('timeline.switch_and_save'), desc: t('timeline.move_desc') },
            { key: 'discard', label: t('timeline.discard_and_switch'), desc: t('timeline.discard_desc') }
          ],
          defaultOptionKey: 'save',
          showCheckbox: true,
          checkboxLabel: t('timeline.always_save_and_switch'),
          checkboxValue: autoSaveEnabled,
          onCheckboxToggle: (v) => setAutoSaveEnabled(v),
          onConfirm: (selectedKey) => {
            if (selectedKey === 'save') {
              addKeyframe(undefined, currentTimeRef.current);
              moveTo(time, triggerScroll);
            } else if (selectedKey === 'move') {
              addKeyframe(undefined, time);
              moveTo(time, triggerScroll);
            } else if (selectedKey === 'discard') {
              moveTo(time, triggerScroll);
            }
          }
        });
      }
    } else {
      moveTo(time, triggerScroll);
    }
  }

  function handleTrackScrub(e: MouseEvent | globalThis.MouseEvent) {
    if (!trackRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left + trackRef.current.scrollLeft;
    let newTime = 0;
    
    let isInsert = false;
    let lineX: number | null = null;

    if (tickLayout.length > 0) {
      let closestIdx = 0;
      let closest = tickLayout[0];
      let minDist = Math.abs(x - tickLayout[0].x);
      
      tickLayout.forEach((layout, idx) => {
        const d = Math.abs(x - layout.x);
        if (d < minDist) {
          minDist = d;
          closest = layout;
          closestIdx = idx;
        }
      });

      const dx = x - closest.x;
      const threshold = closest.width * 0.35;

      if (draggedKeyframeTime !== null && Math.abs(dx) >= threshold) {
        isInsert = true;
        if (dx > 0) {
          if (closestIdx < tickLayout.length - 1) {
            newTime = tickLayout[closestIdx + 1].t;
            lineX = (closest.x + tickLayout[closestIdx + 1].x) / 2;
          } else {
            const interval = metronomeInterval || 0.5;
            newTime = closest.t + interval;
            lineX = closest.x + closest.width / 2;
          }
        } else {
          if (closestIdx > 0) {
            newTime = closest.t;
            lineX = (tickLayout[closestIdx - 1].x + closest.x) / 2;
          } else {
            newTime = 0;
            lineX = closest.x - closest.width / 2;
          }
        }
      } else {
        newTime = closest.t;
      }
    } else {
      newTime = Math.max(0, Math.min(x / (60 * (TICK_WIDTH || 80)), 1)) * duration;
    }
    
    if (metronomeInterval > 0 && tickLayout.length === 0) {
      newTime = Math.round(newTime / metronomeInterval) * metronomeInterval;
    }
    if (e.ctrlKey !== modifierState.ctrl || e.shiftKey !== modifierState.shift) {
      setModifierState({ ctrl: e.ctrlKey || (e as any).metaKey, shift: e.shiftKey });
    }
    if (draggedKeyframeTime !== null) {
      if (newTime !== dragTargetTime) setDragTargetTime(newTime);
      isDragInsertRef.current = isInsert;
      setIsDragInsert(isInsert);
      setInsertLineX(lineX);
    } else {
      if (e.type === 'mousedown') {
        safeMoveTo(newTime);
      } else {
        setCurrentTime(newTime);
        if (audioRef.current && hasMusicLoaded) audioRef.current.currentTime = newTime;
      }
    }
  }

  const handlePrevTick = () => {
    if (ticks.length > 0) {
      let prevTick = ticks[0];
      for (let i = ticks.length - 1; i >= 0; i--) {
        if (ticks[i] < currentTimeRef.current - 0.01) { prevTick = ticks[i]; break; }
      }
      safeMoveTo(prevTick, true);
    } else {
      let step = metronomeInterval > 0 ? metronomeInterval : 0.1;
      let newTime = Math.max(0, currentTimeRef.current - step);
      if (metronomeInterval > 0) newTime = Math.round(newTime / metronomeInterval) * metronomeInterval;
      safeMoveTo(newTime, true);
    }
  };

  const handleJumpToStart = () => safeMoveTo(0, true);
  const handleJumpToEnd = () => safeMoveTo(duration, true);

  const handlePrevPage = () => {
    if (!trackRef.current) return;
    const visibleFrames = Math.floor(trackRef.current.clientWidth / TICK_WIDTH);
    if (tickLayout.length > 0) {
      const currentIdx = tickLayout.findIndex(l => l.t >= currentTimeRef.current - 0.01);
      safeMoveTo(tickLayout[Math.max(0, currentIdx - visibleFrames)].t, true);
    } else {
      safeMoveTo(Math.max(0, currentTimeRef.current - (metronomeInterval || 0.5) * visibleFrames), true);
    }
  };

  const handleNextPage = () => {
    if (!trackRef.current) return;
    const visibleFrames = Math.floor(trackRef.current.clientWidth / TICK_WIDTH);
    if (tickLayout.length > 0) {
      const currentIdx = tickLayout.findIndex(l => l.t >= currentTimeRef.current - 0.01);
      safeMoveTo(tickLayout[Math.min(tickLayout.length - 1, currentIdx + visibleFrames)].t, true);
    } else {
      safeMoveTo(Math.min(duration, currentTimeRef.current + (metronomeInterval || 0.5) * visibleFrames), true);
    }
  };

  const handleNextTick = () => {
    if (ticks.length > 0) {
      let nextTick = ticks[ticks.length - 1];
      for (let i = 0; i < ticks.length; i++) {
        if (ticks[i] > currentTimeRef.current + 0.01) { nextTick = ticks[i]; break; }
      }
      safeMoveTo(nextTick, true);
    } else {
      let step = metronomeInterval > 0 ? metronomeInterval : 0.1;
      let newTime = Math.min(duration, currentTimeRef.current + step);
      if (metronomeInterval > 0) newTime = Math.round(newTime / metronomeInterval) * metronomeInterval;
      safeMoveTo(newTime, true);
    }
  };

  const handleDropPose = (poseId: string, time: number) => {
    const poseEntry = allPoses.find(p => p.id === poseId);
    if (poseEntry) {
      applyPoseToTime(poseEntry.pose, time, poseEntry.cameraState);
    }
  };

  const handleSaveAndNext = () => {
    addKeyframe();
    if (ticks.length > 0) {
      for (let i = 0; i < ticks.length; i++) {
        if (ticks[i] > currentTimeRef.current + 0.01) { safeMoveTo(ticks[i], true); break; }
      }
    } else if (metronomeInterval > 0) {
      const snapped = Math.round(currentTimeRef.current / metronomeInterval) * metronomeInterval;
      safeMoveTo(Math.min(duration, snapped + metronomeInterval), true);
    }
  };

  const isKeyframeAtCurrent = keyframes.some((kf: Keyframe) => Math.abs(kf.time - snappedPlayhead) < 0.05);

  useEffect(() => {
    const handleGlobalKeyDown = (e: globalThis.KeyboardEvent) => {
      const isFeedbackOpen = useDanceStore.getState().isFeedbackOpen;
      const target = e.target as HTMLElement;
      if (isFeedbackOpen || target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        // Only special exception: Allow Escape to blur the input if we wanted, 
        // but for now just return to let normal typing work.
        return;
      }

      if (isMobile) {
        if (e.key === ' ' && !e.ctrlKey && !e.metaKey) {
          e.preventDefault();
          setIsPlaying(!isPlaying);
        }
        return;
      }

      if ((e.ctrlKey || e.metaKey) && e.key === 's') { e.preventDefault(); if (!isPlaying) handleSaveAndNext(); }
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        if (!isPlaying) {
          if (e.shiftKey) redo();
          else undo();
        }
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'y') { e.preventDefault(); if (!isPlaying) redo(); }

      // Copy/Paste Frames
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        if (!isPlaying) {
          if (useDanceStore.getState().selectedFrameTimes.length > 0) {
            useDanceStore.getState().copySelectedFrames();
          } else {
            useDanceStore.getState().copyPose();
          }
        }
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        if (!isPlaying) {
          if (useDanceStore.getState().copiedFrames.length > 0) {
            useDanceStore.getState().insertCopiedFrames(false);
          } else if (useDanceStore.getState().copiedPose) {
            useDanceStore.getState().pastePoseAndSave();
          }
        }
      }

      // Delete Frames
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (!isPlaying && useDanceStore.getState().selectedFrameTimes.length > 0) {
          e.preventDefault();
          useDanceStore.getState().deleteSelectedFrames();
        }
      }

      // Navigation
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrevTick();
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNextTick();
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        const state = useDanceStore.getState();
        const current = state.currentTime;
        const prevKf = [...state.keyframes].reverse().find(kf => kf.time < current - 0.05);
        if (prevKf) safeMoveTo(prevKf.time);
        else safeMoveTo(0);
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const state = useDanceStore.getState();
        const current = state.currentTime;
        const nextKf = state.keyframes.find(kf => kf.time > current + 0.05);
        if (nextKf) safeMoveTo(nextKf.time);
        else safeMoveTo(state.duration);
      }

      if (e.key === ' ' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        setIsPlaying(!isPlaying);
      }
    };
    const handleKeyTracker = (e: globalThis.KeyboardEvent) => setModifierState({ ctrl: e.ctrlKey, shift: e.shiftKey });
    window.addEventListener('keydown', handleGlobalKeyDown);
    window.addEventListener('keydown', handleKeyTracker);
    window.addEventListener('keyup', handleKeyTracker);
    return () => {
      window.removeEventListener('keydown', handleGlobalKeyDown);
      window.removeEventListener('keydown', handleKeyTracker);
      window.removeEventListener('keyup', handleKeyTracker);
    };
  }, [isPlaying, metronomeInterval, duration, keyframes]);

  const handleSaveSequence = (asNew: boolean = false) => {
    const doSave = (id: string | undefined, name: string) => {
      try {
        const sequenceData = {
          id,
          name,
          folderId: isSharedSequence ? UNSORTED_SEQUENCES_ID : targetFolderId,
          duration,
          keyframes: [...keyframes],
          frameMarkers,
          footMarkers: [...footMarkers],
          locationMarkers: [...locationMarkers],
          audioFileName,
          audioDuration,
          audioBpm: bpm,
          audioHash,
          audioSourceUrl,
          metronomeInterval,
          beatSubdivision
        };

        const savedId = saveSequenceStore(sequenceData);
        setLoadedSequence(savedId, name, user?.uid, loadedSequence?.collaborators);
        setHasUnsavedSequenceChanges(false);
      } catch (err) {
        console.error("Error during save operation:", err);
        useDanceStore.getState().confirmAction({
          title: t('timeline.save_failed'),
          message: t('timeline.save_failed_message'),
          isAlert: true,
          onConfirm: () => { }
        });
      }
    };

    if (loadedSequence && !asNew && !isSharedSequence) {
      // Direct overwrite
      doSave(loadedSequence.id, loadedSequence.name);
    } else {
      // Prompt for name
      const timestamp = new Date().toLocaleTimeString().replace(/:/g, '-');
      const defaultName = audioFileName ? audioFileName.replace(/\.[^/.]+$/, "") : (loadedSequence ? loadedSequence.name : `Sequence-${timestamp}`);

      openInputDialog({
        title: (asNew || isSharedSequence) ? t('timeline.save_sequence_as') : t('timeline.save_sequence_title'),
        defaultName,
        folderName: isSharedSequence ? t('inputDialog.unsorted_folder', 'Unsorted') : sequenceFolder?.name,
        notification: (isSharedSequence && !asNew) ? t('inputDialog.shared_sequence_notification') : undefined,
        onSave: (name) => doSave(undefined, name)
      });
    }
  };

  const handleInsertBlankFrames = (before: boolean) => {
    openInputDialog({
      title: t('timeline.insert_blank_frames'),
      defaultName: '4',
      placeholder: t('timeline.insert_blank_frames_placeholder'),
      submitLabel: t('timeline.insert'),
      onSave: (val) => {
        const count = parseInt(val);
        if (!isNaN(count) && count > 0) {
          insertBlankFrames(count, before);
        }
      }
    });
  };

  return (
    <div className="timeline-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
      <div className={`timeline-panel glass-panel ${
        timelineZoom < 0.6 ? 'zoom-very-low' : timelineZoom < 0.8 ? 'zoom-low' : 'zoom-normal'
      }`} ref={panelRef} style={{ '--timeline-zoom': visualZoom } as any}>
        {!isLandscapeMobile && <div
          id="timeline-track-scroll"
          className={`timeline-track ${hasMusicLoaded ? 'has-music' : ''}`}
          ref={trackRef}
          onMouseDown={(e) => { 
            if (e.button !== 0) return;
            setIsDragging(true); 
            setIsPlaying(false); 
            handleTrackScrub(e); 
          }}
          onMouseMove={(e) => { 
            if (isDragging && draggedKeyframeTime === null) {
              handleTrackScrub(e); 
            } 
          }}
          onMouseUp={() => {
            setIsDragging(false); 
          }}
          onMouseLeave={() => { 
            setIsDragging(false); 
          }}
           onScroll={(e) => setTrackScroll(e.currentTarget.scrollLeft)}
          style={{ overflowX: 'auto', overflowY: 'hidden', display: 'block', height: `calc(75px * ${visualZoom})` }}
        >
          {hasMusicLoaded && (
            <canvas id="timeline-waveform-canvas" ref={waveformCanvasRef} className="waveform-canvas" style={{ position: 'absolute', top: `calc(8px * ${visualZoom})`, left: 0, width: totalTrackWidth, height: `calc(59px * ${visualZoom})`, pointerEvents: 'none', zIndex: 20 }} />
          ) }

          <div style={{ position: 'relative', height: '100%', width: totalTrackWidth + 100, minWidth: '100%', pointerEvents: 'none' }}>
            {(() => {
              const visibleLeft = trackScroll - 200;
              const visibleRight = trackScroll + (trackRef.current?.clientWidth || window.innerWidth) + 200;
              
              return tickLayout.map(({ t: tickTime, x, width }, idx) => {
                if (x + width / 2 < visibleLeft || x - width / 2 > visibleRight) return null;
                
                const kf = keyframes.find((k: Keyframe) => Math.abs(k.time - tickTime) < 0.05);
                const isActive = !isPlaying && (actingSource.type === 'timeline' || actingSource.type === 'library') && Math.abs(tickTime - snappedPlayhead) < 0.05;
                
                // Determine if this tick is part of the drag operation
                let isBeingDragged = false;
                let isTarget = false;
                if (draggedKeyframeTime !== null) {
                  const isDraggedInSelection = selectedFrameTimes.some(t => Math.abs(t - draggedKeyframeTime) < 0.05);
                  const draggedTimes = isDraggedInSelection
                    ? selectedFrameTimes.filter(t => keyframes.some(kf => Math.abs(kf.time - t) < 0.05))
                    : [draggedKeyframeTime];

                  isBeingDragged = draggedTimes.some(t => Math.abs(t - tickTime) < 0.05);

                  if (dragTargetTime !== null) {
                    const delta = dragTargetTime - draggedKeyframeTime;
                    const destinationTimes = draggedTimes.map(t => t + delta);
                    isTarget = !isDragInsert && destinationTimes.some(t => Math.abs(t - tickTime) < 0.05);
                  }
                }

                const isMeasureStart = hasMusicLoaded ? measures.includes(tickTime) : (idx % 4 === 0);
                const isCurrentlyCompact = isTimelineCompact && !kf && !isActive;
                return (
                  <TimelineFrame
                    key={`frame-${tickTime.toFixed(3)}`}
                    time={tickTime} left={x} width={width} isActive={isActive} isPlaying={isPlaying} isMeasureStart={isMeasureStart} keyframe={kf} isBeingDragged={isBeingDragged} isTarget={isTarget} modifierState={modifierState} dots={frameMarkers[tickTime.toFixed(3)] || []} hasMusicLoaded={hasMusicLoaded} measures={measures} beatInMeasure={idx % 4} isCompact={isCurrentlyCompact}
                    onDragStart={(e) => { if (kf && !isPlaying && !isMobile) { setDraggedKeyframeTime(tickTime); setDragTargetTime(tickTime); setDragMousePos({ x: e.clientX, y: e.clientY }); setModifierState({ ctrl: e.ctrlKey || e.metaKey, shift: e.shiftKey }); } }}
                    onSelect={(e) => {
                      if (isPlaying) return;
                      if (isMobile) {
                        safeMoveTo(tickTime);
                        return;
                      }
                      // Range selection
                      if (e.shiftKey) {
                        const start = Math.min(currentTimeRef.current, tickTime);
                        const end = Math.max(currentTimeRef.current, tickTime);
                        const range = ticks.filter(tick => tick >= start - 0.01 && tick <= end + 0.01);
                        setSelectedFrameTimes(range);
                      } else if (e.ctrlKey || e.metaKey) {
                        toggleFrameSelection(tickTime, true);
                      } else {
                        safeMoveTo(tickTime);
                      }
                    }}
                    onContextMenu={(e) => {
                      if (isMobile) return;
                      e.preventDefault();
                      const isAlreadySelected = selectedFrameTimes.some(st => Math.abs(st - tickTime) < 0.05);
                      if (!isAlreadySelected) {
                        safeMoveTo(tickTime, false); // Don't scroll on right-click to avoid jumping
                      }
                      const rect = e.currentTarget.getBoundingClientRect();
                      const hasKeyframeNote = !!(kf?.note && kf.note.trim() !== '');
                      openContextMenu(e.clientX, rect.top, [
                        {
                          label: selectedFrameTimes.length > 1 ? t('timeline.delete_frames') : t('timeline.delete_frame'),
                          icon: Trash2,
                          action: () => {
                            confirmAction({
                              title: selectedFrameTimes.length > 1 ? t('timeline.delete_frames_confirm_title') : t('timeline.delete_frame_confirm_title'),
                              message: selectedFrameTimes.length > 1 ? t('timeline.delete_frames_confirm_message', { count: selectedFrameTimes.length }) : t('timeline.delete_frame_confirm_message'),
                              onConfirm: deleteSelectedFrames
                            });
                          },
                          disabled: isPlaying
                        },
                        {
                          label: selectedFrameTimes.length > 1 ? t('timeline.clear_frames') : t('timeline.clear_frame'),
                          icon: Eraser,
                          action: () => {
                            confirmAction({
                              title: selectedFrameTimes.length > 1 ? t('timeline.clear_frames_confirm_title') : t('timeline.clear_frame_confirm_title'),
                              message: selectedFrameTimes.length > 1 ? t('timeline.clear_frames_confirm_message', { count: selectedFrameTimes.length }) : t('timeline.clear_frame_confirm_message'),
                              onConfirm: clearSelectedFrames
                            });
                          },
                          disabled: isPlaying
                        },
                        {
                          label: selectedFrameTimes.length > 1 ? t('timeline.copy_frames') : t('timeline.copy_frame'),
                          icon: Copy,
                          action: copySelectedFrames,
                          disabled: isPlaying
                        },
                        { isDivider: true },
                        {
                          label: hasKeyframeNote ? t('timeline.edit_note', { defaultValue: 'Edit Note' }) : t('timeline.create_note', { defaultValue: 'Create Note' }),
                          icon: MessageSquare,
                          action: () => {
                            setHoveredTime(tickTime);
                            setIsEditingKfNote(true);
                          },
                          disabled: isPlaying
                        },
                        {
                          label: t('timeline.delete_note', { defaultValue: 'Delete Note' }),
                          icon: Trash2,
                          action: () => {
                            deleteKeyframeNote(tickTime);
                          },
                          disabled: isPlaying || !hasKeyframeNote
                        },
                        { isDivider: true },
                        {
                          label: t('timeline.insert_frame_s'),
                          icon: Plus,
                          subItems: [
                            ...(copiedFrames.length > 0 ? [{
                              label: t('timeline.copied_frames'),
                              icon: ClipboardPaste,
                              subItems: [
                                { label: t('timeline.before'), icon: ArrowLeft, action: () => insertCopiedFrames(true) },
                                { label: t('timeline.after'), icon: ArrowRight, action: () => insertCopiedFrames(false) }
                              ]
                            }] : []),
                            {
                              label: t('timeline.blank_frames'),
                              icon: Plus,
                              subItems: [
                                { label: t('timeline.before'), icon: ArrowLeft, action: () => handleInsertBlankFrames(true) },
                                { label: t('timeline.after'), icon: ArrowRight, action: () => handleInsertBlankFrames(false) }
                              ]
                            }
                          ],
                          disabled: isPlaying
                        }
                      ], 'bottom');
                    }}
                    onHover={(h) => {
                      if (isMobile) return;
                      if (h) {
                        handleFrameMouseEnter(tickTime);
                      } else {
                        handleFrameMouseLeave();
                      }
                    }}
                    onToggleDot={(dotIdx) => { if (!isMobile) toggleFrameMarker(tickTime, dotIdx); }}
                    onDropPose={(poseId) => { if (!isMobile) handleDropPose(poseId, tickTime); }}
                    isSelected={selectedFrameTimes.includes(tickTime)}
                  />
                );
              });
            })()}
            <Playhead ticks={ticks} tickLayout={tickLayout} />
            {draggedKeyframeTime !== null && isDragInsert && insertLineX !== null && (
              <div 
                className="timeline-insert-marker"
                style={{
                  position: 'absolute',
                  left: `${insertLineX}px`,
                  top: 0,
                  bottom: 0,
                  width: '4px',
                  transform: 'translateX(-50%)',
                  background: '#fbbf24',
                  boxShadow: '0 0 15px #fbbf24, 0 0 5px #fbbf24',
                  zIndex: 45,
                  pointerEvents: 'none'
                }}
              />
            )}
          </div>
        </div>}

        {/* Music & Metadata Row */}
        {!isLandscapeMobile && <div className="flex-between" style={{ width: '100%', padding: '0 4px', minHeight: '24px' }}>

          <div className="flex-row" style={{ gap: '12px' }}>
            <MusicPanel audioRef={audioRef} />
            {hasMusicLoaded && bpm && (
              <div style={{
                fontSize: '0.7rem',
                color: '#a78bfa',
                background: 'rgba(139, 92, 246, 0.1)',
                padding: '2px 8px',
                borderRadius: '4px',
                border: '1px solid rgba(139, 92, 246, 0.2)',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                height: '24px'
              }}>
                <span style={{ opacity: 0.6, fontWeight: 600 }}>{t('stage.beat_step')}:</span>
                <span style={{ fontWeight: 800, fontFamily: 'monospace' }}>{(60 / bpm).toFixed(3)}s</span>
              </div>
            )}
          </div>
        </div>}

        <div className="flex-between" style={{ padding: '0 4px', gap: '8px', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '2px' }}>


          <div className="flex-row" style={{ gap: '4px' }}>
            {/* Leftmost spacing block */}
            <button id="timeline-jump-start-btn" className="timeline-btn" onClick={handleJumpToStart} disabled={isPlaying} title={t('timeline.jump_to_start', { defaultValue: "Jump to Start" })}><ChevronFirst size={14} fill="currentColor" /></button>
            {!isMobile && (
              <button id="timeline-prev-page-btn" className="timeline-btn" onClick={handlePrevPage} disabled={isPlaying} title={t('timeline.previous_page', { defaultValue: "Previous Page" })}><ChevronsLeft size={14} fill="currentColor" /></button>
            )}
            {!isReadOnly && (
              <button id="timeline-prev-pose-btn" className="timeline-btn" onClick={handlePrevTick} disabled={isPlaying} title={t('timeline.previous_pose', { defaultValue: "Previous Pose" })}><ChevronLeft size={14} fill="currentColor" /></button>
            )}
            <button id="timeline-play-pause" className={`timeline-btn ${isPlaying ? "active" : ""}`} onClick={() => setIsPlaying(!isPlaying)} title={isPlaying ? t('common.pause', { defaultValue: "Pause" }) : t('common.play', { defaultValue: "Play" })}>
              {isPlaying ? <Square size={14} fill="currentColor" /> : <Play size={14} fill="currentColor" />}
            </button>
            {!isReadOnly && (
              <button id="timeline-next-pose-btn" className="timeline-btn" onClick={handleNextTick} disabled={isPlaying} title={t('timeline.next_pose', { defaultValue: "Next Pose" })}><ChevronRight size={14} fill="currentColor" /></button>
            )}
            {!isMobile && (
              <button id="timeline-next-page-btn" className="timeline-btn" onClick={handleNextPage} disabled={isPlaying} title={t('timeline.next_page', { defaultValue: "Next Page" })}><ChevronsRight size={14} fill="currentColor" /></button>
            )}
            <button id="timeline-jump-end-btn" className="timeline-btn" onClick={handleJumpToEnd} disabled={isPlaying} title={t('timeline.end_of_strip', { defaultValue: "End of Strip" })}><ChevronLast size={14} fill="currentColor" /></button>            <LiveTimeDisplay duration={duration} />
            
            {!isMobile && (
              <>
                <div style={{ width: 1, height: 16, background: 'rgba(255,255,255,0.02)', margin: '0 6px' }}></div>
                <div 
                  style={{
                    position: 'relative',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '24px',
                    margin: '0 4px'
                  }}
                >
                  <button
                    id="timeline-settings-toggle-btn"
                    onMouseEnter={handleSettingsEnter}
                    onMouseLeave={handleSettingsLeave}
                    disabled={isPlaying}
                    title={t('timeline.settings_title', { defaultValue: "Timeline Settings" })}
                    style={{
                      background: (isSettingsHovered || isSettingsTutorialActive) ? 'rgba(56, 189, 248, 0.15)' : 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      borderRadius: '6px',
                      padding: '0 8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      cursor: isPlaying ? 'not-allowed' : 'pointer',
                      opacity: isPlaying ? 0.5 : 1,
                      height: '24px',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  >
                    <Timer size={14} color="#38bdf8" />
                    <span style={{
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      color: '#fbbf24',
                      fontFamily: 'monospace'
                    }}>
                      {duration}s / {hasMusicLoaded ? `${beatSubdivision}x` : metronomeInterval === 0 ? 'off' : `${metronomeInterval}s`}
                    </span>
                  </button>

                  {/* Floating Seq & Tick controls popover directly above the button */}
                  <div 
                    onMouseEnter={handleSettingsEnter}
                    onMouseLeave={handleSettingsLeave}
                    style={{
                      position: 'absolute',
                      bottom: 'calc(100% + 6px)',
                      right: '36px',
                      transform: (isSettingsHovered || isSettingsTutorialActive)
                        ? 'translateY(0) scale(1)'
                        : 'translateY(4px) scale(0.95)',
                      opacity: (isSettingsHovered || isSettingsTutorialActive) ? 1 : 0,
                      pointerEvents: (isSettingsHovered || isSettingsTutorialActive) ? 'auto' : 'none',
                      transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                      zIndex: 100,
                      
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '4px 8px',
                      background: 'rgba(15, 23, 42, 0.95)',
                      backdropFilter: 'blur(8px)',
                      border: '1px solid rgba(56, 189, 248, 0.3)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5), 0 0 8px rgba(56, 189, 248, 0.15)',
                      borderRadius: '6px',
                      height: '26px',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {/* Hover bridge to prevent flickering when mouse moves between popover and button */}
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      right: '32px',
                      width: '80px',
                      height: '8px',
                      background: 'transparent'
                    }} />

                    {/* Sleek down arrow pointing at Settings button */}
                    <div style={{
                      position: 'absolute',
                      bottom: '-4px',
                      right: '32px',
                      width: '8px',
                      height: '8px',
                      background: 'rgba(15, 23, 42, 0.95)',
                      borderRight: '1px solid rgba(56, 189, 248, 0.3)',
                      borderBottom: '1px solid rgba(56, 189, 248, 0.3)',
                      transform: 'rotate(45deg)',
                      zIndex: -1
                    }} />

                    {!hasMusicLoaded && (
                      <>
                        <div className="flex-row" style={{ gap: '4px', fontSize: '0.65rem' }}>
                          <span style={{ color: '#94a3b8', fontWeight: 600 }}>{t('timeline.seq')}:</span>
                          <input
                            id="timeline-duration-input"
                            type="number"
                            min="1"
                            max="60"
                            value={tempDuration}
                            onChange={(e) => setTempDuration(e.target.value)}
                            onBlur={() => handleDurationChange(Number(tempDuration))}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleDurationChange(Number(tempDuration)); }}
                            disabled={isPlaying}
                            style={{
                              background: 'rgba(0,0,0,0.3)',
                              border: '1px solid rgba(255,255,255,0.1)',
                              color: '#fbbf24',
                              padding: '0 4px',
                              width: '38px',
                              textAlign: 'center',
                              outline: 'none',
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              borderRadius: '3px',
                              height: '18px'
                            }}
                          />
                          <span style={{ color: '#94a3b8' }}>s</span>
                        </div>
                        <div style={{ width: 1, height: 12, background: 'rgba(255,255,255,0.15)' }}></div>
                      </>
                    )}

                    <div className="flex-row" style={{ gap: '4px', fontSize: '0.65rem' }}>
                      <span style={{ color: '#94a3b8', fontWeight: 600 }}>{hasMusicLoaded ? t('timeline.beat') : t('timeline.tick')}:</span>
                      <select
                        id="timeline-tick-interval-select"
                        value={hasMusicLoaded ? beatSubdivision : metronomeInterval}
                        onChange={(e) => handleTickIntervalChange(Number(e.target.value))}
                        disabled={isPlaying}
                        style={{
                          background: 'rgba(0,0,0,0.3)',
                          border: '1px solid rgba(255,255,255,0.1)',
                          color: '#fbbf24',
                          padding: '0 2px',
                          outline: 'none',
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          cursor: isPlaying ? 'not-allowed' : 'pointer',
                          borderRadius: '3px',
                          height: '18px'
                        }}
                      >
                        {hasMusicLoaded ? (
                          <>
                            <option value={1} style={{ background: '#1e293b' }}>1x {t('timeline.beat')}</option>
                            <option value={2} style={{ background: '#1e293b' }}>2x {t('timeline.beat')} (1/2)</option>
                            <option value={4} style={{ background: '#1e293b' }}>4x {t('timeline.beat')} (1/4)</option>
                          </>
                        ) : (
                          <>
                            <option value={0} style={{ background: '#1e293b' }}>{t('timeline.off')}</option>
                            <option value={0.1} style={{ background: '#1e293b' }}>0.1s</option>
                            <option value={0.25} style={{ background: '#1e293b' }}>0.25s</option>
                            <option value={0.5} style={{ background: '#1e293b' }}>0.5s</option>
                            <option value={1} style={{ background: '#1e293b' }}>1.0s</option>
                            <option value={2} style={{ background: '#1e293b' }}>2.0s</option>
                          </>
                        )}
                      </select>
                    </div>
                  </div>
                </div>

                <div 
                  style={{
                    position: 'relative',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '24px',
                    margin: '0 4px'
                  }}
                >
                  <button
                    id="timeline-compact-toggle-btn"
                    onMouseEnter={handleZoomEnter}
                    onMouseLeave={handleZoomLeave}
                    className={`timeline-btn circle ${isTimelineCompact ? 'active' : ''}`}
                    onClick={() => setIsTimelineCompact(!isTimelineCompact)}
                    title={isTimelineCompact ? t('timeline.expand') : t('timeline.compact')}
                    style={{
                      background: (isZoomHovered || isZoomTutorialActive) ? 'rgba(56, 189, 248, 0.15)' : 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      borderRadius: '50%',
                      width: '20px',
                      height: '20px',
                      minWidth: '20px',
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  >
                    {isTimelineCompact ? <Maximize2 size={12} /> : <Minimize2 size={12} />}
                  </button>

                  {/* Floating Zoom controls popover directly above the Compact button */}
                  <div 
                    onMouseEnter={handleZoomEnter}
                    onMouseLeave={handleZoomLeave}
                    style={{
                      position: 'absolute',
                      bottom: 'calc(100% + 6px)',
                      right: '0',
                      transform: (isZoomHovered || isZoomTutorialActive)
                        ? 'translateY(0) scale(1)'
                        : 'translateY(4px) scale(0.95)',
                      opacity: (isZoomHovered || isZoomTutorialActive) ? 1 : 0,
                      pointerEvents: (isZoomHovered || isZoomTutorialActive) ? 'auto' : 'none',
                      transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                      zIndex: 100,
                      
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '4px 6px',
                      background: 'rgba(15, 23, 42, 0.95)',
                      backdropFilter: 'blur(8px)',
                      border: '1px solid rgba(56, 189, 248, 0.3)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5), 0 0 8px rgba(56, 189, 248, 0.15)',
                      borderRadius: '6px',
                      height: '26px'
                    }}
                  >
                    {/* Hover bridge to prevent flickering when mouse moves between popover and button */}
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      right: '0',
                      width: '20px',
                      height: '8px',
                      background: 'transparent'
                    }} />

                    {/* Sleek down arrow pointing at Compact button */}
                    <div style={{
                      position: 'absolute',
                      bottom: '-4px',
                      right: '6px',
                      width: '8px',
                      height: '8px',
                      background: 'rgba(15, 23, 42, 0.95)',
                      borderRight: '1px solid rgba(56, 189, 248, 0.3)',
                      borderBottom: '1px solid rgba(56, 189, 248, 0.3)',
                      transform: 'rotate(45deg)',
                      zIndex: -1
                    }} />

                    <button
                      id="timeline-zoom-out-btn"
                      className="timeline-btn circle"
                      onClick={() => setTimelineZoom(timelineZoom - 0.1)}
                      disabled={isPlaying || timelineZoom <= 0.41}
                      title={t('timeline.zoom_out')}
                      style={{ width: '18px', height: '18px', padding: 0 }}
                    >
                      <Minus size={10} />
                    </button>
                    <div
                      id="timeline-zoom-reset-btn"
                      className="zoom-percent-display"
                      onClick={() => setTimelineZoom(1)}
                      title={t('timeline.reset_zoom')}
                      style={{ width: '32px', fontSize: '0.65rem', fontWeight: 700, margin: '0 1px' }}
                    >
                      {Math.round(timelineZoom * 100)}%
                    </div>
                    <button
                      id="timeline-zoom-in-btn"
                      className="timeline-btn circle"
                      onClick={() => setTimelineZoom(timelineZoom + 0.1)}
                      disabled={isPlaying || timelineZoom >= 1.29}
                      title={t('timeline.zoom_in')}
                      style={{ width: '18px', height: '18px', padding: 0 }}
                    >
                      <Plus size={10} />
                    </button>
                  </div>
                </div>

                <div 
                  style={{
                    position: 'relative',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '24px',
                    margin: '0 4px'
                  }}
                >
                  <button
                    id="timeline-random-pose-btn"
                    onMouseEnter={handleRandomEnter}
                    onMouseLeave={handleRandomLeave}
                    onClick={() => generateRandomPoseWorkspace()}
                    disabled={isPlaying}
                    title={t('sidebar.random_pose', { defaultValue: "Random Pose" })}
                    style={{
                      background: (isRandomHovered || isTutorialStepActive) ? 'rgba(56, 189, 248, 0.15)' : 'rgba(56, 189, 248, 0.05)',
                      border: '1px solid rgba(56, 189, 248, 0.2)',
                      borderRadius: '6px',
                      padding: '0 8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: isPlaying ? 'not-allowed' : 'pointer',
                      opacity: isPlaying ? 0.5 : 1,
                      height: '24px',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  >
                    <Dices size={14} color="#38bdf8" />
                  </button>

                  {/* Floating Randomness percentage popover directly above the Dice button */}
                  <div 
                    onMouseEnter={handleRandomEnter}
                    onMouseLeave={handleRandomLeave}
                    style={{
                      position: 'absolute',
                      bottom: 'calc(100% + 6px)',
                      left: '50%',
                      transform: (isRandomHovered || isTutorialStepActive) 
                        ? 'translateX(-50%) translateY(0) scale(1)' 
                        : 'translateX(-50%) translateY(4px) scale(0.95)',
                      opacity: (isRandomHovered || isTutorialStepActive) ? 1 : 0,
                      pointerEvents: (isRandomHovered || isTutorialStepActive) ? 'auto' : 'none',
                      transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                      zIndex: 100,
                      
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '4px 6px',
                      background: 'rgba(15, 23, 42, 0.95)',
                      backdropFilter: 'blur(8px)',
                      border: '1px solid rgba(56, 189, 248, 0.3)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5), 0 0 8px rgba(56, 189, 248, 0.15)',
                      borderRadius: '6px',
                      height: '26px'
                    }}
                  >
                    {/* Hover bridge to prevent flickering when mouse moves between popover and button */}
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '32px',
                      height: '8px',
                      background: 'transparent'
                    }} />

                    {/* Sleek down arrow pointing at Dice button */}
                    <div style={{
                      position: 'absolute',
                      bottom: '-4px',
                      left: 'calc(50% - 4px)',
                      width: '8px',
                      height: '8px',
                      background: 'rgba(15, 23, 42, 0.95)',
                      borderRight: '1px solid rgba(56, 189, 248, 0.3)',
                      borderBottom: '1px solid rgba(56, 189, 248, 0.3)',
                      transform: 'rotate(45deg)',
                      zIndex: -1
                    }} />

                    <button
                      className="timeline-btn circle"
                      onClick={() => setRandomnessAmount(randomnessAmount - 5)}
                      disabled={isPlaying || randomnessAmount <= 0}
                      title={t('timeline.less')}
                      style={{ width: '18px', height: '18px', padding: 0 }}
                    >
                      <Minus size={10} />
                    </button>
                    <div
                      id="timeline-randomness-slider-container"
                      className="zoom-percent-display"
                      onClick={() => setRandomnessAmount(10)}
                      title={t('sidebar.reset_val', { defaultValue: "Reset (10%)", value: '10%' })}
                      style={{ width: '28px', fontSize: '0.65rem', fontWeight: 700, margin: '0 1px' }}
                    >
                      {Math.round(randomnessAmount)}%
                    </div>
                    <button
                      className="timeline-btn circle"
                      onClick={() => setRandomnessAmount(randomnessAmount + 5)}
                      disabled={isPlaying || randomnessAmount >= 100}
                      title={t('timeline.more')}
                      style={{ width: '18px', height: '18px', padding: 0 }}
                    >
                      <Plus size={10} />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="flex-row" style={{ gap: '4px', alignItems: 'center' }}>
            {/* Sequence Folder Display */}
            {!isMobile && <div
              title={sequenceFolderPath}
              style={{
                fontSize: '0.7rem',
                fontWeight: 700,
                color: '#38bdf8',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100px',
                height: '24px',
                lineHeight: '24px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'default',
              }}
            >
              <FolderOpen size={10} />
              <span className="folder-name">{sequenceFolderName}</span>
            </div>}
            {!isMobile && <div style={{ width: 1, height: 14, background: 'rgba(255,255,255,0.15)' }} />}
            {/* Sequence Name Display */}
            {!isMobile && <div
              className="sequence-name-display"
              title={loadedSequence ? loadedSequence.name : t('workspace.unsaved_seq')}
              style={{
                fontSize: '0.7rem',
                fontWeight: 700,
                color: loadedSequence ? 'var(--sequence-name-color)' : '#fbbf24',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '120px',
                marginRight: '6px',
                height: '24px',
                lineHeight: '24px',
                display: 'inline-block'
              }}
            >
              {loadedSequence ? (hasUnsavedSequenceChanges ? `${loadedSequence.name}*` : loadedSequence.name) : t('workspace.unsaved')}
            </div>}

            {!isMobile && (
              <>
                <button
                  id="timeline-clear-btn"
                  className="timeline-btn yellow"
                  onClick={() => {
                    useDanceStore.getState().confirmAction({
                      title: t('timeline.clear_timeline_title'),
                      message: t('timeline.clear_timeline_message'),
                      confirmLabel: t('timeline.clear_timeline_title'),
                      cancelLabel: t('common.cancel'),
                      onConfirm: () => clearTimeline()
                    });
                  }}
                  disabled={isPlaying}
                  title={t('timeline.clear_all_keyframes')}
                >
                  <Eraser size={14} />
                </button>
                <div style={{ width: 1, height: 18, background: 'rgba(255,255,255,0.1)', margin: '0 4px' }}></div>
                <div 
                  onMouseEnter={() => setIsSaveHovered(true)}
                  onMouseLeave={() => setIsSaveHovered(false)}
                  style={{
                    position: 'relative',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '24px',
                    margin: '0 4px'
                  }}
                >
                  <button
                    id="timeline-save-sequence"
                    className={`timeline-btn blue ${hasUnsavedSequenceChanges ? 'unsaved' : ''}`}
                    onClick={() => handleSaveSequence(false)}
                    disabled={isPlaying}
                    title={loadedSequence ? t('timeline.update_sequence', { name: loadedSequence.name }) : t('timeline.quick_save_sequence')}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '0 4px' }}>
                      <FolderDown size={14} />
                      <span style={{ fontSize: '0.65rem', fontWeight: 800, letterSpacing: '0.5px' }}>{t('timeline.save_sequence_btn')}</span>
                    </div>
                  </button>

                  {/* Floating Save As New button directly above the Save button */}
                  {loadedSequence && (
                    <div style={{
                      position: 'absolute',
                      bottom: 'calc(100% + 6px)',
                      left: '50%',
                      transform: isSaveHovered
                        ? 'translateX(-50%) translateY(0) scale(1)' 
                        : 'translateX(-50%) translateY(4px) scale(0.95)',
                      opacity: isSaveHovered ? 1 : 0,
                      pointerEvents: isSaveHovered ? 'auto' : 'none',
                      transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                      zIndex: 100,
                      
                      display: 'flex',
                      alignItems: 'center',
                      padding: '4px 6px',
                      background: 'rgba(15, 23, 42, 0.95)',
                      backdropFilter: 'blur(8px)',
                      border: '1px solid rgba(167, 139, 250, 0.4)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5), 0 0 8px rgba(167, 139, 250, 0.15)',
                      borderRadius: '6px',
                      height: '26px',
                      whiteSpace: 'nowrap'
                    }}>
                      {/* Hover bridge to prevent flickering when mouse moves between popover and button */}
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        height: '8px',
                        background: 'transparent'
                      }} />

                      {/* Sleek down arrow pointing at Save button */}
                      <div style={{
                        position: 'absolute',
                        bottom: '-4px',
                        left: 'calc(50% - 4px)',
                        width: '8px',
                        height: '8px',
                        background: 'rgba(15, 23, 42, 0.95)',
                        borderRight: '1px solid rgba(167, 139, 250, 0.4)',
                        borderBottom: '1px solid rgba(167, 139, 250, 0.4)',
                        transform: 'rotate(45deg)',
                        zIndex: -1
                      }} />

                      <button
                        id="timeline-save-sequence-as"
                        className="timeline-btn purple"
                        onClick={() => handleSaveSequence(true)}
                        disabled={isPlaying}
                        title={t('timeline.save_sequence_as', { defaultValue: "Save Sequence As..." })}
                        style={{ height: '18px', padding: '0 6px', display: 'flex', alignItems: 'center' }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <SaveAll size={10} />
                          <span style={{ fontSize: '0.6rem', fontWeight: 800, letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
                            {t('timeline.save_sequence_as_btn', { defaultValue: "As New" })}
                          </span>
                        </div>
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {hoveredTime !== null && (() => {
          const hTime = hoveredTime;
          let kf = keyframes.find((k: Keyframe) => Math.abs(k.time - hTime) < 0.05);
          const isEditingThis = isEditingPoseNote || isEditingKfNote;
          if (!kf && isEditingThis) {
            kf = { time: hTime };
          }
          if (!kf || (!kf.pose && !kf.note?.trim() && !isEditingThis)) return null;
          const tickIdx = ticks.findIndex(t => Math.abs(t - hTime) < 0.05);
          const layout = tickLayout[tickIdx];
          const leftPos = layout
            ? `max(130px, min(calc(${layout.x}px - ${trackScroll}px + 16px), calc(100% - 130px)))`
            : '50%';
          
          const poseNotesText = kf.pose?.notes?.trim() || "";
          const kfNoteText = kf.note?.trim() || "";

          return (
            <PosePreviewBox
              pose={kf.pose}
              cameraState={kf.cameraState}
              style={{ left: leftPos }}
              onMouseEnter={handlePopupMouseEnter}
              onMouseLeave={handlePopupMouseLeave}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <PoseNoteField
                label={t('timeline.pose_note_header', { defaultValue: 'Pose' })}
                value={poseNotesText}
                emptyText={t('timeline.no_pose_note', { defaultValue: 'No pose notes' })}
                disabled={!kf.pose}
                onSave={(note) => setKeyframePoseNotes(hTime, note)}
                onEditingChange={setIsEditingPoseNote}
                classNames={{
                  cell: 'pose-preview-note-cell',
                  headerRow: 'pose-preview-header-row',
                  header: 'pose-preview-note-header pose-header',
                  iconBtn: 'pose-preview-icon-btn pose-btn',
                  body: 'pose-preview-note-body',
                  textarea: 'pose-preview-textarea',
                }}
              />

              <PoseNoteField
                label={t('timeline.keyframe_note_header', { defaultValue: 'Frame' })}
                value={kfNoteText}
                emptyText={t('timeline.no_keyframe_note', { defaultValue: 'No keyframe notes' })}
                onSave={(note) => setKeyframeNote(hTime, note)}
                isEditing={isEditingKfNote}
                onEditingChange={setIsEditingKfNote}
                ids={{ saveBtn: 'keyframe-note-save-btn', textarea: 'keyframe-note-textarea' }}
                classNames={{
                  cell: 'pose-preview-note-cell',
                  headerRow: 'pose-preview-header-row',
                  header: 'pose-preview-note-header keyframe-header',
                  iconBtn: 'pose-preview-icon-btn keyframe-btn',
                  body: 'pose-preview-note-body',
                  textarea: 'pose-preview-textarea',
                }}
              />
            </PosePreviewBox>
          );
        })()}


        {draggedKeyframeTime !== null && dragMousePos !== null && createPortal(
          (() => {
            const isDragCopy = modifierState.ctrl;
            const isDraggedInSelection = selectedFrameTimes.some(t => Math.abs(t - draggedKeyframeTime) < 0.05);
            
            const draggedTimes = isDraggedInSelection
              ? selectedFrameTimes.filter(t => keyframes.some(kf => Math.abs(kf.time - t) < 0.05))
              : [draggedKeyframeTime];
            
            const draggedKfs = keyframes.filter(kf => draggedTimes.some(t => Math.abs(t - kf.time) < 0.05));
            if (draggedKfs.length === 0) return null;

            const previewKfs = draggedKfs.slice(0, 3);
            const hasMore = draggedKfs.length > 3;
            const delta = dragTargetTime !== null ? dragTargetTime - draggedKeyframeTime : 0;

            return (
              <div 
                className={`timeline-drag-preview-card ${isDragCopy ? 'copy-mode' : 'move-mode'}`}
                style={{
                  position: 'fixed',
                  left: `${dragMousePos.x + 12}px`,
                  top: `${dragMousePos.y + 12}px`,
                  pointerEvents: 'none',
                  zIndex: 99999,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '5px',
                  padding: '6px',
                  borderRadius: '8px',
                  background: 'rgba(15, 23, 42, 0.9)',
                  backdropFilter: 'blur(12px)',
                  border: isDragCopy ? '1px solid rgba(34, 197, 94, 0.5)' : '1px solid rgba(56, 189, 248, 0.5)',
                  boxShadow: isDragCopy 
                    ? '0 8px 20px rgba(0, 0, 0, 0.7), 0 0 10px rgba(34, 197, 94, 0.25)' 
                    : '0 8px 20px rgba(0, 0, 0, 0.7), 0 0 10px rgba(56, 189, 248, 0.25)',
                  maxWidth: '110px',
                  color: '#fff',
                  fontFamily: 'inherit'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px', width: '100%' }}>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '2px',
                    padding: '1px 5px',
                    borderRadius: '12px',
                    fontSize: '0.52rem',
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    background: isDragCopy ? 'rgba(34, 197, 94, 0.2)' : 'rgba(56, 189, 248, 0.2)',
                    color: isDragCopy ? '#4ade80' : '#38bdf8',
                    border: isDragCopy ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid rgba(56, 189, 248, 0.3)'
                  }}>
                    {isDragCopy ? <Copy size={8} /> : <ArrowRight size={8} />}
                    {isDragCopy ? 'Copy' : 'Move'}
                  </div>
                  
                  <span style={{ fontSize: '0.55rem', color: 'rgba(255,255,255,0.7)', fontWeight: 700 }}>
                    {draggedKfs.length} {draggedKfs.length === 1 ? 'Fr' : 'Frs'}
                  </span>
                </div>

                <div style={{
                  position: 'relative',
                  height: '46px',
                  width: '100%',
                  margin: previewKfs.length > 1 ? '6px 0' : '2px 0',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  {previewKfs.map((kf, index) => {
                    const rotation = (index - (previewKfs.length - 1) / 2) * 5;
                    const offsetX = (index - (previewKfs.length - 1) / 2) * 5;
                    const offsetY = (previewKfs.length - 1 - index) * 3;
                    
                    return (
                      <div
                        key={`preview-kf-${kf.time}`}
                        style={{
                          position: 'absolute',
                          width: '44px',
                          height: '44px',
                          borderRadius: '5px',
                          border: isDragCopy ? '0.75px solid rgba(34, 197, 94, 0.4)' : '0.75px solid rgba(56, 189, 248, 0.4)',
                          background: 'rgba(30, 41, 59, 0.95)',
                          boxShadow: '0 3px 6px rgba(0,0,0,0.5)',
                          overflow: 'hidden',
                          transform: `rotate(${rotation}deg) translate(${offsetX}px, ${offsetY}px)`,
                          zIndex: 10 + index,
                          opacity: 1 - (previewKfs.length - 1 - index) * 0.15,
                          pointerEvents: 'none'
                        }}
                      >
                        {kf.pose ? (
                          <ThumbnailView pose={kf.pose} cameraState={kf.cameraState} scaleFactor={0.3} />
                        ) : (
                          <div className="flex-center" style={{ width: '100%', height: '100%', color: '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <MessageSquare size={16} />
                          </div>
                        )}
                        <div style={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          fontSize: '0.45rem',
                          fontWeight: 800,
                          textAlign: 'center',
                          background: 'rgba(15, 23, 42, 0.85)',
                          color: '#fff',
                          padding: '0.5px 0',
                          fontFamily: 'monospace'
                        }}>
                          {kf.time.toFixed(1)}s
                        </div>
                      </div>
                    );
                  })}

                  {hasMore && (
                    <div style={{
                      position: 'absolute',
                      right: '4px',
                      bottom: '-3px',
                      background: '#fbbf24',
                      color: '#0f172a',
                      fontSize: '0.5rem',
                      fontWeight: 900,
                      padding: '0.5px 3px',
                      borderRadius: '10px',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
                      zIndex: 100
                    }}>
                      +{draggedKfs.length - previewKfs.length}
                    </div>
                  )}
                </div>

                {dragTargetTime !== null && (
                  <div style={{
                    fontSize: '0.55rem',
                    fontWeight: 800,
                    color: '#fbbf24',
                    textAlign: 'center',
                    background: 'rgba(251, 191, 36, 0.08)',
                    border: '1px solid rgba(251, 191, 36, 0.25)',
                    borderRadius: '4px',
                    padding: '2.5px',
                    width: '100%',
                    marginTop: '1px',
                    fontFamily: 'monospace'
                  }}>
                    {isDragInsert ? (
                      `Drop between ${(dragTargetTime - metronomeInterval).toFixed(1)}s and ${dragTargetTime.toFixed(1)}s`
                    ) : (
                      `Drop at ${dragTargetTime.toFixed(1)}s`
                    )}
                    {draggedKfs.length > 1 && (
                      <span style={{ color: 'rgba(255,255,255,0.4)', fontWeight: 500, fontSize: '0.48rem', display: 'block', marginTop: '1px', fontFamily: 'inherit' }}>
                        (Shifted {delta >= 0 ? '+' : ''}{delta.toFixed(1)}s)
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })(),
          document.body
        )}
      </div>
      {hasMusicLoaded && audioUrl && (
        <audio ref={audioRef} src={audioUrl || undefined} preload="auto" style={{ display: 'none' }} />
      )}
    </div>
  );
};

// End of file

